import { z } from "zod";
import { IdempotencyKeySchema, IdSchema, IsoDateTimeSchema, Sha256Schema } from "./primitives.js";
export const ModelRouteSchema = z.enum(["fast", "deep"]);
export const ModelRouteReasonCodeSchema = z.enum([
    "requested_deep",
    "high_source_count",
    "high_problem_count",
    "large_note",
    "missing_required_facts",
    "fast_path_default",
]);
export const ModelReleaseSchema = z
    .object({
    releaseId: IdSchema,
    tenantId: IdSchema,
    version: z.number().int().positive(),
    route: ModelRouteSchema,
    provider: z.string().min(1).max(80),
    modelId: z.string().min(1).max(160),
    promptVersion: z.string().min(1).max(160),
    dataClassification: z.enum(["synthetic", "phi_eligible"]),
    createdBy: IdSchema,
    createdAt: IsoDateTimeSchema,
    contentHash: Sha256Schema,
})
    .strict();
export const ModelReleaseStageReceiptSchema = z
    .object({
    release: ModelReleaseSchema,
    status: z.literal("staged"),
})
    .strict();
export const ModelEvaluationCaseSchema = z
    .object({
    caseId: IdSchema,
    category: z.enum(["gold", "adversarial"]),
    passed: z.boolean(),
    structuredOutputValid: z.boolean(),
    unsafeOmissionCount: z.number().int().nonnegative(),
    unsupportedActionCount: z.number().int().nonnegative(),
    blockingSafetyIssueCount: z.number().int().nonnegative(),
    latencyMs: z.number().int().nonnegative().max(120_000),
})
    .strict()
    .superRefine((testCase, context) => {
    const objectivelyPassed = testCase.structuredOutputValid &&
        testCase.unsafeOmissionCount === 0 &&
        testCase.unsupportedActionCount === 0 &&
        testCase.blockingSafetyIssueCount === 0;
    if (testCase.passed !== objectivelyPassed) {
        context.addIssue({
            code: "custom",
            path: ["passed"],
            message: "Evaluation pass state must match its deterministic safety outcomes",
        });
    }
});
export const ModelEvaluationSuiteResultSchema = z
    .object({
    suiteId: IdSchema,
    tenantId: IdSchema,
    releaseId: IdSchema,
    releaseVersion: z.number().int().positive(),
    releaseContentHash: Sha256Schema,
    evaluatedBy: IdSchema,
    evaluatedAt: IsoDateTimeSchema,
    cases: z.array(ModelEvaluationCaseSchema).min(2).max(2_000),
    total: z.number().int().positive(),
    passed: z.number().int().nonnegative(),
    failed: z.number().int().nonnegative(),
    p95LatencyMs: z.number().int().nonnegative(),
    zeroFailure: z.boolean(),
    contentHash: Sha256Schema,
})
    .strict();
export const ModelEvaluationSuiteReceiptSchema = ModelEvaluationSuiteResultSchema;
export const ModelPromotionReviewSchema = z
    .object({
    reviewId: IdSchema,
    tenantId: IdSchema,
    siteId: IdSchema,
    releaseId: IdSchema,
    releaseVersion: z.number().int().positive(),
    releaseContentHash: Sha256Schema,
    evaluationSuiteId: IdSchema,
    evaluationSuiteContentHash: Sha256Schema,
    reviewerId: IdSchema,
    reviewerRole: z.literal("physician"),
    reviewerAal: z.literal("aal2"),
    decision: z.enum(["approved", "rejected"]),
    clinicalAttestation: z.string().min(20).max(4_000),
    reviewedAt: IsoDateTimeSchema,
})
    .strict();
export const ModelReleasePinSchema = z
    .object({
    route: ModelRouteSchema,
    releaseId: IdSchema,
    releaseVersion: z.number().int().positive(),
    releaseContentHash: Sha256Schema,
    provider: z.string().min(1).max(80),
    modelId: z.string().min(1).max(160),
    promptVersion: z.string().min(1).max(160),
    dataClassification: z.enum(["synthetic", "phi_eligible"]),
    evaluationSuiteId: IdSchema,
    evaluationSuiteContentHash: Sha256Schema,
    promotionReviewId: IdSchema,
})
    .strict();
export const ModelRoutingThresholdsSchema = z
    .object({
    maxSourcesForFast: z.number().int().positive().max(1_000),
    maxProblemsForFast: z.number().int().positive().max(1_000),
    maxNoteBytesForFast: z.number().int().positive().max(1_000_000),
})
    .strict();
export const ModelRoutingPolicyRefSchema = z
    .object({
    policyId: IdSchema,
    policyVersion: z.number().int().positive(),
    policyContentHash: Sha256Schema,
})
    .strict();
export const ModelKillSwitchScopeSchema = z.enum([
    "all_ai",
    "external_models",
    "deep_path",
    "approved_execution",
]);
export const ModelKillSwitchStateSchema = z
    .object({
    scope: ModelKillSwitchScopeSchema,
    version: z.number().int().nonnegative(),
    blocked: z.boolean(),
    reason: z.string().min(1).max(1_000),
    changedBy: IdSchema.optional(),
    changedAt: IsoDateTimeSchema.optional(),
    contentHash: Sha256Schema.optional(),
})
    .strict();
export const ModelRunDescriptorSchema = z
    .object({
    route: ModelRouteSchema,
    reasonCodes: z.array(ModelRouteReasonCodeSchema).min(1).max(10),
    release: ModelReleasePinSchema,
    policy: ModelRoutingPolicyRefSchema,
    killSwitchVersions: z
        .object({
        allAi: z.number().int().nonnegative(),
        externalModels: z.number().int().nonnegative(),
        deepPath: z.number().int().nonnegative(),
        approvedExecution: z.number().int().nonnegative(),
    })
        .strict(),
})
    .strict();
export const ModelRoutingContextSchema = z
    .object({
    intent: z.enum(["synthesize_plan", "draft_referral", "draft_orders", "draft_discharge"]),
    requestedRoute: z.enum(["auto", "fast", "deep"]),
    sourceCount: z.number().int().nonnegative(),
    problemCount: z.number().int().nonnegative(),
    missingRequiredFactCount: z.number().int().nonnegative(),
    noteBytes: z.number().int().nonnegative().max(1_000_000),
})
    .strict();
export const ModelRoutingPolicySnapshotSchema = z
    .object({
    tenantId: IdSchema,
    siteId: IdSchema,
    policy: ModelRoutingPolicyRefSchema,
    thresholds: ModelRoutingThresholdsSchema,
    pins: z.array(ModelReleasePinSchema).min(1).max(2),
    changeKind: z.enum(["bootstrap", "promotion", "rollback"]),
    activatedAt: IsoDateTimeSchema,
})
    .strict();
export const ModelGovernanceStateSchema = z
    .object({
    routing: ModelRoutingPolicySnapshotSchema.nullable(),
    killSwitches: z.array(ModelKillSwitchStateSchema).length(4),
})
    .strict();
export const ModelPromotionReceiptSchema = z
    .object({
    reviewId: IdSchema,
    release: ModelReleasePinSchema,
    activated: z.boolean(),
    previousRelease: ModelReleasePinSchema.optional(),
    policy: ModelRoutingPolicyRefSchema.optional(),
    promotedAt: IsoDateTimeSchema,
})
    .strict();
export const ModelKillSwitchCommandSchema = z
    .object({
    blocked: z.boolean(),
    expectedVersion: z.number().int().nonnegative(),
    reason: z.string().min(10).max(1_000),
    idempotencyKey: IdempotencyKeySchema,
})
    .strict();
export const ModelRollbackProposalSchema = z
    .object({
    rollbackId: IdSchema,
    route: ModelRouteSchema,
    targetReleaseId: IdSchema,
    targetReleaseVersion: z.number().int().positive(),
    targetReleaseContentHash: Sha256Schema,
    expectedPolicyVersion: z.number().int().positive(),
    reason: z.string().min(20).max(2_000),
    idempotencyKey: IdempotencyKeySchema,
})
    .strict();
export const ModelRollbackProposalReceiptSchema = z
    .object({
    rollbackId: IdSchema,
    requestContentHash: Sha256Schema,
    route: ModelRouteSchema,
    targetReleaseId: IdSchema,
    targetReleaseVersion: z.number().int().positive(),
    expectedPolicyVersion: z.number().int().positive(),
    requestedBy: IdSchema,
    requestedAt: IsoDateTimeSchema,
    status: z.literal("pending_dual_authorization"),
})
    .strict();
export const ModelRollbackApprovalSchema = z
    .object({
    expectedRequestContentHash: Sha256Schema,
    idempotencyKey: IdempotencyKeySchema,
})
    .strict();
export const ModelRollbackReceiptSchema = z
    .object({
    rollbackId: IdSchema,
    requestContentHash: Sha256Schema,
    approvedBy: IdSchema,
    approvedAt: IsoDateTimeSchema,
    previousRelease: ModelReleasePinSchema,
    activeRelease: ModelReleasePinSchema,
    policy: ModelRoutingPolicyRefSchema,
    status: z.literal("completed"),
})
    .strict();
//# sourceMappingURL=model-governance.js.map